import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Compressor from 'compressorjs';

import { ButtonBack } from '../../components/ButtonBack';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { HelmetComponent } from '../../components/Helmet';
import { Notify, NotifyUpdate } from '../../components/Notify';

import * as routes from '../../constants/Routes';
import { useAuth } from '../../hooks/useAuth';
import { usePostValidation } from '../../hooks/usePostValidation';
import {
  IPostValidationNetworkState,
  IPostValidationPublication,
  PostValidationSocialMedia,
} from '../../interfaces/IPostValidation';
import iconInstagram from '../../assets/images/instagram.png';
import iconFacebook from '../../assets/images/facebook.png';

const NETWORK_LABELS: Record<PostValidationSocialMedia, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

// TikTok/YouTube ainda não têm um asset de logo no projeto — usando um ícone
// genérico do Material Icons como placeholder até termos os arquivos reais.
const NETWORK_ICONS: Record<
  PostValidationSocialMedia,
  { image: string } | { icon: string }
> = {
  instagram: { image: iconInstagram },
  facebook: { image: iconFacebook },
  tiktok: { icon: 'music_note' },
  youtube: { icon: 'smart_display' },
};

const toAbsoluteUrl = (url: string) =>
  url?.startsWith('http') ? url : `https://${url}`;

const RATING_STARS = [1, 2, 3, 4, 5];

function StarRatingDisplay({ value }: { value: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, verticalAlign: 'middle' }}>
      {RATING_STARS.map((star) => (
        <i
          key={star}
          className="material-icons-outlined"
          style={{
            fontSize: 16,
            color: star <= value ? '#f5a623' : '#ccc',
          }}
        >
          {star <= value ? 'star' : 'star_border'}
        </i>
      ))}
    </span>
  );
}

const STATUS_LABELS: Record<string, { label: string; badge: string }> = {
  not_submitted: { label: 'Pendente', badge: 'secondary' },
  pending: { label: 'Em validação', badge: 'warning' },
  approved: { label: 'Validado', badge: 'success' },
  rejected: { label: 'Reprovado', badge: 'danger' },
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

function compressFile(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: 0.7,
      success: (result) => {
        resolve(new File([result], file.name, { type: result.type }));
      },
      error: reject,
    });
  });
}

export function PostValidation() {
  const { publicationId } = useParams();
  const { client, player } = useAuth();
  const { getPlayerPublicationById, createSubmission, uploadScreenshot } =
    usePostValidation();

  const [publication, setPublication] = useState<IPostValidationPublication>();
  const [isLoading, setIsLoading] = useState(true);
  const [coverImageFailed, setCoverImageFailed] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File[]>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchPublication();
  }, [publicationId]);

  const fetchPublication = async () => {
    if (!publicationId) return;
    setIsLoading(true);
    const response = await getPlayerPublicationById(publicationId);
    setPublication(response);
    setIsLoading(false);
  };

  const handleSelectFiles = (network: string, files: FileList | null) => {
    if (!files) return;
    const validFiles: File[] = [];

    Array.from(files).forEach((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        Notify('Envie apenas imagens PNG ou JPG.', 'error');
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        Notify('Cada imagem deve ter no máximo 5MB.', 'error');
        return;
      }
      validFiles.push(file);
    });

    setSelectedFiles((prev) => ({
      ...prev,
      [network]: [...(prev[network] || []), ...validFiles],
    }));
  };

  const handleRemoveFile = (network: string, index: number) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [network]: (prev[network] || []).filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (network: string) => {
    const files = selectedFiles[network];
    if (!files || files.length === 0 || !publicationId) return;

    setIsSubmitting((prev) => ({ ...prev, [network]: true }));
    const toastId = Notify('Enviando prints...', 'loading');

    try {
      const screenshotUrls: string[] = [];
      for (const file of files) {
        const compressed = await compressFile(file);
        const url = await uploadScreenshot(compressed);
        if (url) screenshotUrls.push(url);
      }

      await createSubmission({
        publicationId,
        socialMedia: network,
        screenshotUrls,
      });

      NotifyUpdate(
        'Prints enviados! Aguarde a validação da equipe.',
        'success',
        toastId
      );
      setSelectedFiles((prev) => ({ ...prev, [network]: [] }));
      await fetchPublication();
    } catch (error) {
      NotifyUpdate(
        typeof error === 'string'
          ? error
          : 'Não foi possível enviar os prints. Tente novamente.',
        'error',
        toastId
      );
    } finally {
      setIsSubmitting((prev) => ({ ...prev, [network]: false }));
    }
  };

  const networksEntries = Object.entries(publication?.networks || {}) as [
    PostValidationSocialMedia,
    IPostValidationNetworkState,
  ][];

  const pointsFor = (network: PostValidationSocialMedia) =>
    publication?.pointsPerSocialMedia?.[network] ?? 0;

  const totalPoints = networksEntries.reduce(
    (sum, [network]) => sum + pointsFor(network),
    0
  );
  const confirmedPoints = networksEntries
    .filter(([, state]) => state.status === 'approved')
    .reduce(
      (sum, [network, state]) =>
        sum + (state.pointsAwarded ?? pointsFor(network)),
      0
    );
  const pendingPoints = networksEntries
    .filter(([, state]) => state.status === 'pending')
    .reduce((sum, [network]) => sum + pointsFor(network), 0);

  return (
    <>
      <HelmetComponent
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={publication?.title || 'Validador de posts'}
      />
      <div className="container">
        <div className="row">
          <ButtonBack title="Validador de posts" />
        </div>

        {!isLoading && publication && (
          <>
            <div className="row">
              <div className="my-3">
                <Card shadow>
                  {publication.image && !coverImageFailed && (
                    <img
                      src={publication.image}
                      alt={publication.title}
                      className="img-fluid mb-3"
                      style={{
                        width: '100%',
                        borderRadius: 10,
                      }}
                      onError={() => setCoverImageFailed(true)}
                    />
                  )}
                  <span className="fs-5 fw-semibold d-block mb-2">
                    {publication.title}
                  </span>
                  {publication.description && (
                    <p className="mb-3">{publication.description}</p>
                  )}
                  {publication.rules && (
                    <div className="mb-3">
                      <span className="fw-semibold d-block mb-1">Regras</span>
                      <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                        {publication.rules}
                      </p>
                    </div>
                  )}
                  <div className="d-flex justify-content-between mb-2">
                    <span>
                      {confirmedPoints} de {totalPoints} pontos confirmados
                    </span>
                  </div>
                  <div className="progress" style={{ height: 10 }}>
                    <div
                      className="progress-bar bg-success"
                      style={{
                        width: `${totalPoints ? (confirmedPoints / totalPoints) * 100 : 0}%`,
                      }}
                    />
                    <div
                      className="progress-bar bg-warning"
                      style={{
                        width: `${totalPoints ? (pendingPoints / totalPoints) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </Card>
              </div>
            </div>

            <div className="row">
              {networksEntries.map(([network, state]) => {
                const points = pointsFor(network);
                const statusInfo =
                  STATUS_LABELS[state.status] || STATUS_LABELS.not_submitted;
                const hasHandle = Boolean(player?.socialMedia?.[network]);
                const canSubmit =
                  (state.status === 'not_submitted' ||
                    state.status === 'rejected') &&
                  hasHandle;
                const filesForNetwork = selectedFiles[network] || [];

                const icon = NETWORK_ICONS[network];

                return (
                  <div className="col-12 my-2" key={network}>
                    <Card shadow>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="d-flex align-items-center fs-6 fw-semibold">
                          {'image' in icon ? (
                            <img
                              src={icon.image}
                              alt={NETWORK_LABELS[network]}
                              style={{
                                width: 20,
                                height: 20,
                                marginRight: 8,
                                borderRadius: 4,
                              }}
                            />
                          ) : (
                            <i
                              className="material-icons-outlined me-2"
                              style={{ fontSize: 20 }}
                            >
                              {icon.icon}
                            </i>
                          )}
                          {NETWORK_LABELS[network]} · vale {points} pontos
                        </span>
                        <span className={`badge text-bg-${statusInfo.badge}`}>
                          {statusInfo.label}
                        </span>
                      </div>

                      {state.status === 'rejected' && (
                        <div className="alert alert-danger py-2 px-3 mb-3">
                          {state.rejectionReason ||
                            'Os prints enviados não puderam ser validados. Envie novamente.'}
                        </div>
                      )}

                      {state.status === 'approved' && (
                        <div className="alert alert-success py-2 px-3 mb-3">
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <span>
                              +{state.pointsAwarded ?? points} pontos creditados
                            </span>
                            {state.rating != null && (
                              <StarRatingDisplay value={state.rating} />
                            )}
                          </div>
                          {state.approvalDescription && (
                            <div className="mt-2">
                              {state.approvalDescription}
                            </div>
                          )}
                        </div>
                      )}

                      {state.status === 'pending' && (
                        <div className="alert alert-warning py-2 px-3 mb-3">
                          Aguardando validação da equipe
                        </div>
                      )}

                      {!hasHandle &&
                        (state.status === 'not_submitted' ||
                          state.status === 'rejected') && (
                          <div className="alert alert-secondary py-2 px-3 mb-3">
                            Cadastre o @ do seu {NETWORK_LABELS[network]} no{' '}
                            <Link to={routes.EDIT_PROFILE}>seu perfil</Link>{' '}
                            para poder enviar prints dessa rede.
                          </div>
                        )}

                      {state.screenshotUrls &&
                        state.screenshotUrls.length > 0 && (
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {state.screenshotUrls.map((url) => (
                              <img
                                key={url}
                                src={toAbsoluteUrl(url)}
                                alt="print enviado"
                                style={{
                                  width: 80,
                                  height: 80,
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                }}
                              />
                            ))}
                          </div>
                        )}

                      {canSubmit && (
                        <>
                          {filesForNetwork.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mb-3">
                              {filesForNetwork.map((file, index) => (
                                <div
                                  key={`${file.name}-${index}`}
                                  className="position-relative"
                                >
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    style={{
                                      width: 60,
                                      height: 60,
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn-close position-absolute top-0 end-0"
                                    style={{
                                      transform: 'translate(50%, -50%)',
                                    }}
                                    onClick={() =>
                                      handleRemoveFile(network, index)
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                          <label
                            className="d-flex flex-column align-items-center justify-content-center border rounded p-3 mb-3"
                            style={{ cursor: 'pointer' }}
                          >
                            <i className="material-icons-outlined">upload</i>
                            <span>Toque para enviar o print (PNG ou JPG)</span>
                            <input
                              type="file"
                              accept="image/png,image/jpeg"
                              multiple
                              hidden
                              onChange={(e) =>
                                handleSelectFiles(network, e.target.files)
                              }
                            />
                          </label>

                          <Button
                            text={
                              isSubmitting[network]
                                ? 'Enviando...'
                                : 'Enviar para validação'
                            }
                            color="primary"
                            disabled={
                              filesForNetwork.length === 0 ||
                              isSubmitting[network]
                            }
                            isLoading={isSubmitting[network]}
                            onClick={() => handleSubmit(network)}
                          />
                        </>
                      )}
                    </Card>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}
